import {
  BUSINESS_PAYFORM_ROUTES,
  buildAdminPaymentDomainsPath,
  ADMIN_PUBLIC_EXPERIENCE_ROUTES,
  buildBusinessPaymentDomainsPath,
  buildManagedAccountMigrationPath,
  buildAdminPayformsEligibilityPath,
  buildAdminPaymentDomainsEnsurePath,
  buildBusinessPaymentDomainsEnsurePath,
  buildManagedAccountMigrationPreviewPath,
} from '@am3nd-org/pointcraft-types';

import { apiAcceptVersion } from 'src/utils/app-version';
import { installRequestDeadline } from 'src/utils/http/request-deadline';
import axios, { AxiosError, AxiosRequestConfig } from 'src/utils/http/tracked-axios';
import {
  buildDeviceIdentityHeaders,
  encodeDeviceContextHeaders,
} from 'src/utils/http/device-metadata-headers';

import { useSystemStore } from 'src/store/SystemStore';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import useAuthStore from 'src/auth/store/firebase/auth-store';
import { useDeviceContextStore } from 'src/store/DeviceContext';
import { isOfflineDemoActiveAtBoot } from 'src/features/offline-demo/boot';
import { handleOfflineDemoRequest } from 'src/features/offline-demo/request-handler';
import {
  AuthOperationError,
  classifyAuthOperationError,
} from 'src/auth/errors/auth-operation-error';
import {
  clearOperatorSession,
  OPERATOR_SESSION_HEADER,
  type OperatorAuthenticationLockReason,
  getValidOperatorSessionTokenForRequest,
} from 'src/store/OperatorAuthentication';
import {
  isFirstPartyApiRequest,
  reportFirstPartyGatewayFailure,
  reportFirstPartyHtmlBodyResponse,
  reportFirstPartyTransportFailure,
} from 'src/features/connectivity/first-party-transport-signal';

// ----------------------------------------------------------------------

function createApiClient(config: AxiosRequestConfig) {
  const configuredApiBaseURL = useSystemStore.getState().baseUrl;
  return installRequestDeadline(axios.create(config), (request) =>
    isFirstPartyApiRequest(request.url ?? '', request.baseURL ?? '', configuredApiBaseURL)
  );
}

// axiosInstance.interceptors.response.use(
//   (res) => res,
//   (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
// );

// ----------------------------------------------------------------------

const INVALIDATING_OPERATOR_ERROR_CODES = new Map<string, OperatorAuthenticationLockReason>([
  ['OPERATOR_AUTHENTICATION_REQUIRED', 'SESSION_REQUIRED'],
  ['OPERATOR_NOT_ACTIVE', 'OPERATOR_NOT_ACTIVE'],
  ['OPERATOR_SESSION_CONTEXT_MISMATCH', 'SESSION_CONTEXT_MISMATCH'],
  ['OPERATOR_SESSION_EXPIRED', 'EXPIRED'],
  ['OPERATOR_SESSION_INVALID', 'SESSION_INVALID'],
]);

interface OperatorErrorPayload {
  errorCode?: unknown;
  message?: unknown;
}

function getOperatorErrorPayload(error: unknown): OperatorErrorPayload | null {
  if (!error || typeof error !== 'object') return null;

  const { response } = error as { response?: { data?: unknown } };
  const data = response?.data;
  return data && typeof data === 'object' ? (data as OperatorErrorPayload) : null;
}

export function handleOperatorAuthenticationError(error: unknown): boolean {
  const payload = getOperatorErrorPayload(error);
  const errorCode = typeof payload?.errorCode === 'string' ? payload.errorCode : null;
  if (!errorCode?.startsWith('OPERATOR_')) return false;

  const reason = INVALIDATING_OPERATOR_ERROR_CODES.get(errorCode);
  if (reason) {
    clearOperatorSession({
      reason,
      errorCode,
      message: typeof payload?.message === 'string' ? payload.message : undefined,
    });
  }

  return true;
}

export { isFirstPartyApiRequest } from 'src/features/connectivity/first-party-transport-signal';

function reportRequestTransportFailure(error: unknown, url: string, requestBaseURL: string): void {
  const configuredApiBaseURL = useSystemStore.getState().baseUrl;
  if (isFirstPartyApiRequest(url, requestBaseURL, configuredApiBaseURL)) {
    if (!reportFirstPartyTransportFailure(error)) {
      reportFirstPartyGatewayFailure(error);
    }
  }
}

function reportRequestHtmlBodyResponse(
  res: { headers?: unknown; data?: unknown },
  url: string,
  requestBaseURL: string
): void {
  const configuredApiBaseURL = useSystemStore.getState().baseUrl;
  if (isFirstPartyApiRequest(url, requestBaseURL, configuredApiBaseURL)) {
    reportFirstPartyHtmlBodyResponse(res);
  }
}

function getOperatorSessionHeaders(
  url: string,
  requestBaseURL: string,
  requiresAuth: boolean,
  includeOperatorSession = true
): Record<string, string> {
  const configuredApiBaseURL = useSystemStore.getState().baseUrl;
  if (
    !requiresAuth ||
    !includeOperatorSession ||
    !isFirstPartyApiRequest(url, requestBaseURL, configuredApiBaseURL)
  ) {
    return {};
  }

  const operatorSessionToken = getValidOperatorSessionTokenForRequest();
  return operatorSessionToken ? { [OPERATOR_SESSION_HEADER]: operatorSessionToken } : {};
}

function getSafeAxiosErrorDetails(error: unknown, url: string) {
  const axiosError = error as AxiosError;
  const payload = getOperatorErrorPayload(error);
  return {
    url,
    status: axiosError.response?.status,
    errorCode: typeof payload?.errorCode === 'string' ? payload.errorCode : undefined,
  };
}

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  if (isOfflineDemoActiveAtBoot()) {
    return handleOfflineDemoRequest({ url, method: 'GET', data: config });
  }
  const baseURL = useSystemStore.getState().baseUrl;
  console.log('[Fetcher] Starting fetch for:', url);

  try {
    const { getToken, authenticated } = useAuthStore.getState();

    // Log auth state for debugging
    console.log('[Fetcher] Auth state check:', { authenticated, url });

    if (!authenticated) {
      console.error('[Fetcher] ⚠️ User not authenticated - cannot fetch');
      throw new AuthOperationError('User not authenticated', {
        kind: 'AUTHENTICATION',
        code: 'AUTH_STATE_UNAUTHENTICATED',
      });
    }

    console.log('[Fetcher] Getting auth token...');
    let authToken: string | null = null;

    try {
      authToken = await getToken();
    } catch (tokenError) {
      console.error('[Fetcher] ⚠️ Failed to get auth token:', tokenError);
      if (tokenError instanceof AuthOperationError) throw tokenError;
      throw new AuthOperationError(
        'Failed to get auth token',
        classifyAuthOperationError(tokenError),
        { cause: tokenError }
      );
    }

    if (!authToken) {
      console.error('[Fetcher] ⚠️ No auth token available');
      throw new AuthOperationError('authToken is not found', {
        kind: 'AUTHENTICATION',
        code: 'EMPTY_AUTH_TOKEN',
      });
    }

    console.log('[Fetcher] ✅ Auth token obtained successfully');

    const axiosInstance = createApiClient({
      baseURL,
      timeout: 30000, // 30 second timeout to prevent hanging indefinitely
    });

    console.log('[Fetcher] Making API request to:', url);
    const res = await axiosInstance.get(url, {
      ...config,
      headers: {
        Authorization: `Bearer ${authToken}`,
        ...getMetaHeaders(),
        ...getOperatorSessionHeaders(url, baseURL, true),
        ...config?.headers,
      },
    });

    console.log('[Fetcher] ✅ Success for:', url);
    reportRequestHtmlBodyResponse(res, url, baseURL);
    const data = res.data.result;
    return data;
  } catch (error) {
    console.error('[Fetcher] ❌ Error fetching:', url);
    reportRequestTransportFailure(error, url, baseURL);

    // Operator-session failures must never be mistaken for primary-auth failures.
    const isOperatorAuthenticationError = handleOperatorAuthenticationError(error);
    if (isOperatorAuthenticationError) throw error;

    const axiosError = error as AxiosError;

    // Log more details about the error
    if (axiosError.isAxiosError) {
      console.error('[Fetcher] Axios error details:', getSafeAxiosErrorDetails(error, url));
    } else {
      console.error('[Fetcher] Non-axios error:', error);
    }

    const failure = classifyAuthOperationError(error);
    if (failure.kind !== 'DATA' && failure.kind !== 'UNKNOWN') {
      throw new AuthOperationError('Authenticated request failed', failure, { cause: error });
    }
    throw error;
  }
};

// ----------------------------------------------------------------------

/**
 * Default deadline for the first-party senders. A black-holed connection must
 * settle as a transport failure (feeding the connectivity confirmation signal)
 * instead of hanging a caller forever. Pass `timeout: 0` (axios semantics: no
 * timeout) on a known long-running call to disable the deadline explicitly.
 */
export const API_SENDER_DEFAULT_TIMEOUT_MS = 60_000;

export const apiSender = async (
  url: string,
  method: ApiMethods,
  data: any,
  formatResponse = true
) => {
  if (isOfflineDemoActiveAtBoot()) {
    return handleOfflineDemoRequest({ url, method, data });
  }
  const baseURL = useSystemStore.getState().baseUrl;
  const axiosInstance = createApiClient({ baseURL, timeout: API_SENDER_DEFAULT_TIMEOUT_MS });
  const { getToken } = useAuthStore.getState();
  const authToken = await getToken();
  if (!authToken) {
    throw new Error('authToken is not found');
  }
  const headers = {
    Authorization: `Bearer ${authToken}`,
    ...getMetaHeaders(),
    ...getOperatorSessionHeaders(url, baseURL, true),
  };
  try {
    // Update this to all methods
    let res;
    if (method === ApiMethods.GET) {
      res = await axiosInstance.get(url, {
        headers,
      });
    } else if (method === ApiMethods.POST) {
      res = await axiosInstance.post(url, data, {
        headers,
      });
    } else if (method === ApiMethods.PUT) {
      res = await axiosInstance.put(url, data, {
        headers,
      });
    } else if (method === ApiMethods.DELETE) {
      res = await axiosInstance.delete(url, {
        headers,
        data, // Include request body for DELETE requests
      });
    } else if (method === ApiMethods.PATCH) {
      res = await axiosInstance.patch(url, data, {
        headers,
      });
    } else {
      throw new Error('Method not supported');
    }
    // const res = await axiosInstance.(url, data, {
    //   headers: { Authorization: `Bearer ${authToken}` },
    // });
    reportRequestHtmlBodyResponse(res, url, baseURL);

    if (res?.status >= 300) {
      return null;
    }
    return formatResponse ? (res.data.results ?? res.data.result) : res.data;
  } catch (error) {
    reportRequestTransportFailure(error, url, baseURL);
    handleOperatorAuthenticationError(error);
    return null;
  }
};

const getMetaHeaders = (): any => {
  const { currentDeviceType, appVersion, baseAthUrl } = useSystemStore.getState();
  const business = useDeviceSyncStore.getState().businessData;
  const deviceContextStore = useDeviceContextStore.getState();
  const appDeviceContext = deviceContextStore.getAppDeviceAccountContext();
  const businessDeviceId = deviceContextStore.businessDeviceId ?? '';
  const businessId = deviceContextStore.businessId ?? business?.id;

  // Encode device data to Base64 to avoid non-ASCII character issues in headers
  const contextHeaders = encodeDeviceContextHeaders(
    appDeviceContext ?? {
      ...(deviceContextStore.accountNumber
        ? { accountNumber: deviceContextStore.accountNumber }
        : {}),
      businessId: businessId ?? '',
      businessDeviceId,
      businessLocationId: deviceContextStore.businessLocationId ?? '',
      terminalId: deviceContextStore.terminalId,
      deviceFingerprint: deviceContextStore.deviceFingerprint ?? '',
      publicKeyFingerprint: deviceContextStore.publicKeyFingerprint ?? '',
      deviceName: deviceContextStore.deviceName ?? null,
      deviceType: deviceContextStore.deviceType ?? currentDeviceType,
      environment: deviceContextStore.environment,
      lastResolvedAt: deviceContextStore.lastResolvedAt,
    }
  );
  return {
    ...buildDeviceIdentityHeaders(
      { ...deviceContextStore, businessId, businessDeviceId },
      currentDeviceType,
      business?.name
    ),
    ...contextHeaders,
    'accept-version': apiAcceptVersion(appVersion.current),
    'minimum-version': appVersion.minimum,
    'X-Base-Ath-Url': baseAthUrl,
  };
};

export const apiSenderV2 = async (
  url: string,
  method: ApiMethods,
  data: any,
  formatResponse = true,
  token = null,
  options?: {
    signal?: AbortSignal;
    /** Recheck caller session/scope immediately before dispatch, including after token refresh. */
    assertAdmission?: () => void;
    /** Milliseconds; defaults to API_SENDER_DEFAULT_TIMEOUT_MS. Pass 0 to disable for known long-running calls. */
    timeout?: number;
    throwOnError?: boolean;
    requiresAuth?: boolean;
    withCredentials?: boolean;
    headers?: Record<string, string>;
    /** Prevent credential-bearing request/response diagnostics for this call. */
    sensitiveRequest?: boolean;
    sensitiveResponse?: boolean;
    /** Defaults to true. Recovery/bootstrap endpoints can explicitly opt out. */
    includeOperatorSession?: boolean;
    baseURL?: string;
  }
) => {
  options?.assertAdmission?.();
  if (isOfflineDemoActiveAtBoot()) {
    return handleOfflineDemoRequest({ url, method, data });
  }
  const baseURL = options?.baseURL ?? useSystemStore.getState().baseUrl;
  const axiosInstance = createApiClient({
    baseURL,
    timeout: options?.timeout ?? API_SENDER_DEFAULT_TIMEOUT_MS,
  });
  const requiresAuth = options?.requiresAuth !== false;
  const metaHeaders = requiresAuth ? getMetaHeaders() : {};
  let authToken: string | null = null;
  if (requiresAuth) {
    const { getToken } = useAuthStore.getState();
    authToken = token ?? (await getToken());
    if (!authToken) {
      throw new Error('authToken is not found');
    }
  }
  const headers = {
    ...(requiresAuth ? { Authorization: `Bearer ${authToken}` } : {}),
    ...metaHeaders,
    ...getOperatorSessionHeaders(
      url,
      baseURL,
      requiresAuth,
      options?.includeOperatorSession !== false
    ),
    ...options?.headers,
  };
  options?.assertAdmission?.();
  try {
    // Update this to all methods
    let res;
    if (method === ApiMethods.GET) {
      res = await axiosInstance.get(url, {
        headers,
        signal: options?.signal,
        withCredentials: options?.withCredentials,
        pointcraftDebugSensitiveRequest: options?.sensitiveRequest,
      });
    } else if (method === ApiMethods.POST) {
      res = await axiosInstance.post(url, data, {
        headers,
        signal: options?.signal,
        withCredentials: options?.withCredentials,
        pointcraftDebugSensitiveRequest: options?.sensitiveRequest,
      });
    } else if (method === ApiMethods.PUT) {
      res = await axiosInstance.put(url, data, {
        headers,
        signal: options?.signal,
        withCredentials: options?.withCredentials,
        pointcraftDebugSensitiveRequest: options?.sensitiveRequest,
      });
    } else if (method === ApiMethods.DELETE) {
      res = await axiosInstance.delete(url, {
        headers,
        data, // Include request body for DELETE requests
        signal: options?.signal,
        withCredentials: options?.withCredentials,
        pointcraftDebugSensitiveRequest: options?.sensitiveRequest,
      });
    } else if (method === ApiMethods.PATCH) {
      res = await axiosInstance.patch(url, data, {
        headers,
        signal: options?.signal,
        withCredentials: options?.withCredentials,
        pointcraftDebugSensitiveRequest: options?.sensitiveRequest,
      });
    } else {
      throw new Error('Method not supported');
    }
    // const res = await axiosInstance.(url, data, {
    //   headers: { Authorization: `Bearer ${authToken}` },
    // });
    reportRequestHtmlBodyResponse(res, url, baseURL);
    if (res?.status === 400) {
      return res.data;
    }

    if (res?.status >= 300) {
      return null;
    }
    const returnValue = formatResponse
      ? (res.data.results ?? res.data.result ?? res.data)
      : res.data;
    if (!options?.sensitiveRequest && !options?.sensitiveResponse) {
      console.log('AZED apiSenderV2 returning:', {
        formatResponse,
        returnValue,
        resData: res.data,
        url,
      });
    }
    return returnValue;
  } catch (error) {
    reportRequestTransportFailure(error, url, baseURL);
    handleOperatorAuthenticationError(error);
    if (options?.throwOnError) {
      const failure = classifyAuthOperationError(error);
      if (failure.kind !== 'DATA' && failure.kind !== 'UNKNOWN') {
        throw new AuthOperationError('API request failed', failure, { cause: error });
      }
      throw error;
    }
    const axiosError = error as AxiosError;
    if (axiosError.isAxiosError) {
      if (!options?.sensitiveRequest && !options?.sensitiveResponse) {
        console.warn('[apiSenderV2] Request failed', getSafeAxiosErrorDetails(error, url));
      }
      return axiosError.response?.data;
    }
    // console.log("AZED found error results", error)
    return null;
  }
};
export const apiSenderWithErrors = async (url: string, _method: ApiMethods, data: any) => {
  if (isOfflineDemoActiveAtBoot()) {
    return handleOfflineDemoRequest({ url, method: _method, data });
  }
  const baseURL = useSystemStore.getState().baseUrl;
  const axiosInstance = createApiClient({ baseURL });
  const { authToken } = useAuthStore.getState();
  if (!authToken) {
    throw new Error('authToken is not found');
  }

  let res;
  try {
    res = await axiosInstance.post(url, data, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        ...getMetaHeaders(),
        ...getOperatorSessionHeaders(url, baseURL, true),
      },
    });
  } catch (error) {
    reportRequestTransportFailure(error, url, baseURL);
    handleOperatorAuthenticationError(error);
    throw error;
  }
  // const res = await axiosInstance(url, {
  //   method,
  //   data,
  //   headers: { Authorization: `Bearer ${authToken}` },
  // });
  reportRequestHtmlBodyResponse(res, url, baseURL);
  if (res?.status >= 300) {
    return null;
  }
  return res.data.results;
};
// ----------------------------------------------------------------------

export enum ApiMethods {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

// ----------------------------------------------------------------------

/* =================== PUBLIC EXPERIENCES ADMIN PATHS =================== */

/**
 * Substitutes `:param` placeholders in a frozen
 * `ADMIN_PUBLIC_EXPERIENCE_ROUTES` pattern. Values are URL-encoded, and an
 * unsubstituted placeholder throws rather than producing a request to a
 * literal `:experienceId` path.
 */
const buildAdminPublicExperiencePath = (
  pattern: string,
  params: Record<string, string>
): string => {
  const path = Object.entries(params).reduce(
    (current, [key, value]) => current.replaceAll(`:${key}`, encodeURIComponent(value)),
    pattern
  );
  if (path.includes('/:')) {
    throw new Error(`Unsubstituted parameter in admin public-experiences path: ${path}`);
  }
  return path;
};

/* =================== PAYFORMS MERCHANT PATHS =================== */

/**
 * Same substitution over the frozen `BUSINESS_PAYFORM_ROUTES` patterns
 * (contract 2.0.0). These routes carry NO `:businessId` — the business is
 * derived server-side from the merchant session — so the only parameters are
 * resource ids the merchant addresses.
 */
const buildBusinessPayformPath = (pattern: string, params: Record<string, string> = {}): string => {
  const path = Object.entries(params).reduce(
    (current, [key, value]) => current.replaceAll(`:${key}`, encodeURIComponent(value)),
    pattern
  );
  if (path.includes('/:')) {
    throw new Error(`Unsubstituted parameter in payforms path: ${path}`);
  }
  return path;
};

export const apiEndpoints = {
  server: {
    preloadData: '/api/server/preload',
  },
  device: {
    /**
     * Device diagnostic log batches. Device-authenticated; the endpoint derives
     * business and device from the principal. Excluded from the device-local
     * API history and from diagnostic network capture — recording the upload of
     * the logs is a feedback loop. See src/features/diagnostics.
     */
    diagnosticLogs: '/api/device/logs',
  },
  auth: {
    user: '/api/auth/user',
    login: '/api/auth/login',
    signup: '/api/auth/signup',
  },
  user: {
    // Account Access V2: verify a raw PIN against hashed user PINs server-side.
    // See pointcraft-backend/docs/frontend/verify-user-pin-api.md.
    verifyPin: '/api/user/verify-pin',
    // The signed-in user's own account (any role). See
    // pointcraft-backend/docs/frontend/user-self-service-api.md.
    profile: '/api/user/profile',
    profileIdentifierCode: '/api/user/profile/identifier/code',
    profileIdentifierVerify: '/api/user/profile/identifier/verify',
    pin: '/api/user/pin',
    pinCode: '/api/user/pin/code',
    notificationPreferences: '/api/user/notification-preferences',
  },
  admin: {
    fetchSales: '/api/admin/sales',
    fetchSingleSale: (salesId: string, businessId: string) =>
      `/api/admin/sales/${salesId}/${businessId}`,
    updateSale: (salesId: string) => `/api/admin/business/v2/sales/${salesId}`,
    deleteSale: (salesId: string) => `/api/admin/business/v2/sales/${salesId}`,
    restoreSale: (salesId: string) => `/api/admin/business/v2/sales/${salesId}/restore`,
    fetchUsers: '/api/admin/users',
    fetchBusinesses: '/api/admin/business',
    fetchBusinessesV2: '/api/admin/business/v2',
    fetchBusinessesV2Paginated: '/api/admin/business/v2/paginated',
    fetchBusinessesV3: '/api/admin/business/v3',
    fetchBusinessesV3Paginated: '/api/admin/business/v3/paginated',
    fetchBusinessV3ById: (id: string) => `/api/admin/business/v3/${id}`,
    // Account Access V2 authorization (ceiling + roles). GET reads, PATCH partial-updates.
    businessAccessV2: (id: string) => `/api/admin/business/v3/access/${id}`,
    // Account Access V2 user management. See admin-business-v3-api.md.
    businessUsersV3: (businessId: string) => `/api/admin/business/v3/users/${businessId}`,
    businessUserProfileV3: (businessId: string, userId: string) =>
      `/api/admin/business/v3/users/${businessId}/${userId}`,
    businessUserRoleV3: (businessId: string, userId: string) =>
      `/api/admin/business/v3/users/${businessId}/${userId}/role`,
    businessUserPinV3: (businessId: string, userId: string) =>
      `/api/admin/business/v3/users/${businessId}/${userId}/pin`,
    businessUserPinLockV3: (businessId: string, userId: string) =>
      `/api/admin/business/v3/users/${businessId}/${userId}/pin-lock`,
    businessUserStatusV3: (businessId: string, userId: string) =>
      `/api/admin/business/v3/users/${businessId}/${userId}/status`,
    businessUserVerifyPinV3: (businessId: string, userId: string) =>
      `/api/admin/business/v3/users/${businessId}/${userId}/verify-pin`,
    businessUserNotificationPreferencesV3: (businessId: string, userId: string) =>
      `/api/admin/business/v3/users/${businessId}/${userId}/notification-preferences`,
    linkTerminalToLocation: '/api/admin/business/v2/devices/linking',
    updateTerminal: (terminalId: string) => `/api/admin/business/v2/devices/${terminalId}`,
    updateBusiness: (id: string) => `/api/admin/business/v2/${id}`,
    fetchBusinessTransactions: (id: string) => `/api/admin/business/v2/${id}/transactions`,
    fetchSecureCode: (id: string) => `/api/admin/business/v2/${id}/secure-code`,
    fetchBusinessBalance: (id: string) => `/api/admin/business/v2/payouts/balance?businessId=${id}`,
    fetchBusinessPayouts: (id: string) => `/api/admin/business/v2/payouts?businessId=${id}`,
    createInstantPayout: '/api/admin/business/v2/payouts',
    togglePaymentAccountStatus: (id: string) =>
      `/api/admin/business/v2/${id}/payment-accounts/toggle`,
    importPaymentAccount: (id: string) => `/api/admin/business/v2/${id}/payment-accounts/import`,
    importStripePaymentAccount: (businessId: string) =>
      `/api/admin/business/v2/onboarding/stripe/${businessId}/import`,
    updatePaymentAccount: (businessId: string, paymentAccountId: string) =>
      `/api/admin/business/v2/${businessId}/payment-accounts/${paymentAccountId}`,
    fetchBlockChypCredentials: (businessId: string) =>
      `/api/admin/business/v2/${businessId}/payment-accounts/blockchyp/credentials`,
    generateBlockChypCredentials: (businessId: string) =>
      `/api/admin/business/v2/${businessId}/payment-accounts/blockchyp/credentials/generate`,
    createPaxProviderAccount: (id: string) => `/api/admin/business/v2/${id}/pax-provider-accounts`,
    fetchSingleBusiness: (id: string) => `/api/admin/business/${id}`,
    linkUserToBusiness: (userId: string, businessId: string) =>
      `/api/admin/business/${businessId}/link-user/${userId}`,
    reportsByRange: '/api/admin/reports/daily/range',
    calculateDailySummary: '/api/admin/business/v2/daily-summary/calculate',
    createUser: '/api/admin/users/new-user',
    createBusiness: '/api/admin/business/new-business',
    linkUserAndCreateStp: '/api/admin/business/link-create-stp',
    createAccountV3: '/api/admin/business/v2/create-account',
    importAccount: '/api/admin/business/v2/import-account',
    createMinimalBusiness: '/api/admin/business/v2/onboarding/platform',
    createStripeAccount: '/api/admin/business/v2/onboarding/stripe',
    paymentsInternal: '/api/admin/payments/internal',
    paymentsExternal: '/api/admin/payments/external',
    accountPayouts: '/api/admin/business/v2/account-payouts',
    accountPayoutById: (id: string) => `/api/admin/business/v2/account-payouts/${id}`,
    allSales: '/api/admin/business/v2/sales',
    recalculateSales: '/api/admin/business/v2/sales/recalculate',
    processorEvents: '/api/admin/diagnostics/processor-events',
    rerunProcessorEvent: '/api/admin/diagnostics/processor-events/rerun',
    dailySummaries: '/api/admin/business/v2/daily-summary',
    updateDailySummary: '/api/admin/business/v2/daily-summary/update',
    manualOverrideDailySummary: '/api/admin/business/v2/daily-summary/manual-override',
    bulkRecalculateDailySummary: '/api/admin/business/v2/daily-summary/bulk-recalculate',
    monthlyDailySummary: '/api/admin/business/v2/daily-summary/month',
    // TOTP endpoints
    totpGenerate: '/api/admin/totp/generate',
    totpVerify: '/api/admin/totp/verify',
    totpDisable: '/api/admin/totp',
    totpSecrets: '/api/admin/totp',
    // Notification endpoints
    notifications: '/api/admin/notifications',
    notificationTemplates: '/api/admin/notifications/templates',
    sendTemplateNotification: '/api/admin/notifications/send-template',
    resendNotification: (id: string) => `/api/admin/notifications/${id}/resend`,
    batchResendNotifications: '/api/admin/notifications/resend-batch',
    // Registration endpoints
    submitRegistration: '/api/admin/business/v2/registration/form',
    registrationSecureUploadLink: (registrationId: string) =>
      `/api/admin/business/v2/registration/form/secure-upload-link?registrationId=${encodeURIComponent(registrationId)}`,
    // Donate page setup
    setupDonatePage: '/api/admin/donate/setup',
    // Orphan payments endpoints
    orphanPayments: '/api/admin/orphan-payments',
    resolveOrphanPayment: (id: string) => `/api/admin/orphan-payments/${id}/resolve`,
    // Payment transactions endpoints
    paymentTransactions: '/api/admin/business/v2/payment-transactions',
    paymentTransactionById: (id: string) => `/api/admin/business/v2/payment-transactions/${id}`,
    // Processor transactions endpoint (for reconciliation)
    processorTransactions: '/api/admin/business/v2/payment-transactions/processor',
    // Sync processor transactions endpoint (manual sync from processor to database)
    syncProcessorTransactions: '/api/admin/business/v2/payment-transactions/sync',
    // Sync processor transactions by ID endpoint (sync specific transactions by their IDs)
    syncProcessorTransactionsById: '/api/admin/business/v2/payment-transactions/sync-by-id',
    // QuickBooks sales backfill endpoint (backfill historical daily sales into QuickBooks)
    quickbooksBackfill: '/api/admin/reports/quickbooks/backfill',
    quickbooks: {
      status: (businessId: string) =>
        `/api/admin/quickbooks/status?businessId=${encodeURIComponent(businessId)}`,
      reauthUrl: (businessId: string) =>
        `/api/admin/quickbooks/reauth-url?businessId=${encodeURIComponent(businessId)}`,
      callback: '/api/admin/quickbooks/callback',
    },
    /**
     * Public Experiences MVP1 (admin, super-admin only). Paths are built from
     * the frozen `ADMIN_PUBLIC_EXPERIENCE_ROUTES` patterns in
     * `@am3nd-org/pointcraft-types`, so a contract rename cannot drift here.
     * Every route carries `/business/:businessId` by construction — the admin
     * authors experiences for any business and there is no implicit tenant.
     */
    publicExperiences: {
      collection: (businessId: string) =>
        buildAdminPublicExperiencePath(ADMIN_PUBLIC_EXPERIENCE_ROUTES.collection, { businessId }),
      item: (businessId: string, experienceId: string) =>
        buildAdminPublicExperiencePath(ADMIN_PUBLIC_EXPERIENCE_ROUTES.item, {
          businessId,
          experienceId,
        }),
      publish: (businessId: string, experienceId: string) =>
        buildAdminPublicExperiencePath(ADMIN_PUBLIC_EXPERIENCE_ROUTES.publish, {
          businessId,
          experienceId,
        }),
      disable: (businessId: string, experienceId: string) =>
        buildAdminPublicExperiencePath(ADMIN_PUBLIC_EXPERIENCE_ROUTES.disable, {
          businessId,
          experienceId,
        }),
      eligibility: (businessId: string, experienceId: string) =>
        buildAdminPublicExperiencePath(ADMIN_PUBLIC_EXPERIENCE_ROUTES.eligibility, {
          businessId,
          experienceId,
        }),
      submissions: (businessId: string, experienceId: string) =>
        buildAdminPublicExperiencePath(ADMIN_PUBLIC_EXPERIENCE_ROUTES.submissions, {
          businessId,
          experienceId,
        }),
      submissionItem: (businessId: string, experienceId: string, submissionId: string) =>
        buildAdminPublicExperiencePath(ADMIN_PUBLIC_EXPERIENCE_ROUTES.submissionItem, {
          businessId,
          experienceId,
          submissionId,
        }),
      cancelSubscription: (businessId: string, experienceId: string, submissionId: string) =>
        buildAdminPublicExperiencePath(ADMIN_PUBLIC_EXPERIENCE_ROUTES.cancelSubscription, {
          businessId,
          experienceId,
          submissionId,
        }),
      reconcileCheckout: (businessId: string, experienceId: string, checkoutId: string) =>
        buildAdminPublicExperiencePath(ADMIN_PUBLIC_EXPERIENCE_ROUTES.reconcileCheckout, {
          businessId,
          experienceId,
          checkoutId,
        }),
    },
    /**
     * Super-admin managed-account migration (Part 2, DEC-P2-16). Paths come from
     * the frozen `ADMIN_STRIPE_ACCOUNT_MIGRATION_ROUTES` builders in
     * `@am3nd-org/pointcraft-types`, so a contract rename cannot drift here.
     */
    stripeManagedAccountMigration: {
      payformsEligibility: (businessId: string) => buildAdminPayformsEligibilityPath(businessId),
      migrationPreview: (businessId: string) =>
        buildManagedAccountMigrationPreviewPath(businessId),
      migrate: (businessId: string) => buildManagedAccountMigrationPath(businessId),
      // Payment-method domains of the linked account (DEC-P2-18 / BE-P2-14).
      paymentDomains: (businessId: string) => buildAdminPaymentDomainsPath(businessId),
      paymentDomainsEnsure: (businessId: string) =>
        buildAdminPaymentDomainsEnsurePath(businessId),
    },
    stripePayoutCard: {
      status: (businessId: string) => `/api/admin/business/v3/stripe/${businessId}/payout-card`,
      handoffs: (businessId: string) =>
        `/api/admin/business/v3/stripe/${businessId}/payout-card/handoffs`,
      handoff: (businessId: string, handoffId: string) =>
        `/api/admin/business/v3/stripe/${businessId}/payout-card/handoffs/${handoffId}`,
      accountSession: (businessId: string, handoffId: string) =>
        `/api/admin/business/v3/stripe/${businessId}/payout-card/handoffs/${handoffId}/account-session`,
      complete: (businessId: string, handoffId: string) =>
        `/api/admin/business/v3/stripe/${businessId}/payout-card/handoffs/${handoffId}/complete`,
    },
    stripeBleTesting: {
      context: (businessId: string) =>
        `/api/admin/testing/stripe-terminal/context/${encodeURIComponent(businessId)}`,
      payment: '/api/admin/testing/stripe-terminal/payment',
      paymentStatus: (businessId: string, paymentIntentId: string) =>
        `/api/admin/testing/stripe-terminal/payment/${encodeURIComponent(paymentIntentId)}?businessId=${encodeURIComponent(businessId)}`,
      cancelPayment: (businessId: string, paymentIntentId: string) =>
        `/api/admin/testing/stripe-terminal/payment/${encodeURIComponent(paymentIntentId)}/cancel?businessId=${encodeURIComponent(businessId)}`,
    },
    // Admin transaction reconciliation endpoints
    reconciliationRuns: '/api/admin/reconciliation/runs',
    reconciliationRun: (runId: string) =>
      `/api/admin/reconciliation/runs/${encodeURIComponent(runId)}`,
    reconciliationRunRecords: (runId: string) =>
      `/api/admin/reconciliation/runs/${encodeURIComponent(runId)}/records`,
    // Public business profile endpoints
    publicBusinessProfile: (businessId: string) => `/api/admin/business/v2/public/${businessId}`,
    // Marketplace admin endpoints
    marketplace: {
      distributors: '/api/admin/marketplace/distributors',
      distributorById: (id: string) => `/api/admin/marketplace/distributors/${id}`,
      products: '/api/admin/marketplace/products',
      productById: (id: string) => `/api/admin/marketplace/products/${id}`,
      productsImport: '/api/admin/marketplace/products/import',
      orders: '/api/admin/marketplace/orders',
      orderById: (id: string) => `/api/admin/marketplace/orders/${id}`,
      orderStatus: (id: string) => `/api/admin/marketplace/orders/${id}/status`,
    },
    // Auth V3 Device Management — read-only admin endpoints that power the Device
    // Management dashboard (summary counts + four paginated list tables). The claim
    // (approve) action lives in `apiEndpoints.businessDevicesV3.claim`.
    // See docs/authentication/device-management-admin-client.md.
    deviceManagement: {
      summary: '/api/admin/auth/v3/device-management/summary',
      pairingSessions: '/api/admin/auth/v3/device-management/pairing-sessions',
      businessDevices: '/api/admin/auth/v3/device-management/business-devices',
      paymentTerminals: '/api/admin/auth/v3/device-management/payment-terminals',
      businessLocations: '/api/admin/auth/v3/device-management/business-locations',
    },
  },
  checkout: {
    sale: '/api/sales',
    sale_v2: '/api/sales/v2',
    cancelSale: '/api/sales/v2/cancel-sale',
    manual_check: '/api/sales/manual-check',
    pay: '/api/sales/payment',
    fetch: '/api/sales',
    terminalCancel: '/api/sales/terminal/clear',
    terminalStatus: '/api/sales/terminal/status',
    // Customer-facing cart display on the payment terminal (provider-agnostic;
    // BlockChyp only today). Backend resolves provider + credentials server-side.
    terminalDisplay: '/api/sales/terminal/display',
  },
  // v4 terminal-routed checkout flow (create sale → create payment(s) →
  // poll sale-level payment-status). See pointcraft-backend
  // docs/v4-payment-flow-client-guide.md.
  v4: {
    sale: '/api/v4/sale',
    checkoutPrepare: '/api/v4/checkout/prepare',
    completedSaleReports: '/api/v4/checkout/completed-reports',
    operatorSession: '/api/v4/auth/operator/session',
    operatorSessionActivity: '/api/v4/auth/operator/session/activity',
    operatorPinRecovery: '/api/v4/auth/operator/pin/recovery',
    deviceSync: '/api/v4/system/device-sync',
    terminal: '/api/v4/system/terminal',
    terminalStatus: '/api/v4/system/terminal/status',
    saleById: (saleId: string) => `/api/v4/sale/${saleId}`,
    cancelSale: (saleId: string) => `/api/v4/sale/${saleId}/cancel`,
    saleCancellationRequest: (saleId: string) => `/api/v4/sale/${saleId}/cancel-request`,
    paymentStatus: (saleId: string) => `/api/v4/sale/${saleId}/payment-status`,
    paymentRecovery: (saleId: string) => `/api/v4/sale/${saleId}/payment-recovery`,
    paymentRecoveryById: (saleId: string, recoveryId: string) =>
      `/api/v4/sale/${saleId}/payment-recovery/${recoveryId}`,
    finalizationRecovery: (saleId: string) => `/api/v4/sale/${saleId}/finalization-recovery`,
    payment: '/api/v4/payment',
    localUpdate: '/api/v4/payment/local-update',
    refund: (transactionId: string) => `/api/v4/payment/${transactionId}/refund`,
    stripeConnectionToken: '/api/v4/payment/stripe/connection-token',
    // Live provider history merged newest-first across enabled provider accounts.
    providerPayments: '/api/v4/providers/payment',
    // Read-only history/list/search endpoint powering the v4 transactions page.
    salesHistory: '/api/v4/sales/history',
    // Full read-only sale detail (checkoutData + split payments/refunds + aggregate).
    saleDetail: (saleId: string) => `/api/v4/sale/${saleId}/detail`,
    saleDetailByIdentifier: (type: 'sale' | 'transaction' | 'providerTransaction', id: string) =>
      `/api/v4/sale/detail/${type}/${encodeURIComponent(id)}`,
  },
  transactions: {
    fetch: '/api/sales',
    fetchV2: '/api/sales/v2',
    fetchV2Records: '/api/sales/v2/records',
    refundV2: '/api/sales/v2/refund',
    addCustomerV2: '/api/sales/v2/add-customer',
    fetchOne: '/api/sales/single',
    support: '/api/sales/support',
    paymentRefund: '/api/sales/payment/refund',
    ebt: '/api/transactions/ebt',
    saleStatusLightweight: '/api/sales/v2/status',
  },
  business: {
    fetchBusinessBalance: '/api/business/balance',
    support: '/api/business/support',
    fees: '/api/business/fees',
    profile: '/api/business/profile',
    accessV2Settings: '/api/business/access/v2/settings',
    itemCategories: '/api/business/item-categories',
    devices: (businessId: string) => `/api/business/devices/${businessId}`,
    companionDevice: '/api/business/devices/companion',
    blockchypCredentials: '/api/business/payment-accounts/blockchyp/credentials',
    paxCredentials: '/api/business/payment-accounts/pax/credentials',
    paymentRecheck: '/api/business/payment-recheck/by-sale',
    integrationsStatus: '/api/business/integrations/status',
    /**
     * Payforms merchant self-service (Public Experiences Part 2). Built from
     * the frozen `BUSINESS_PAYFORM_ROUTES` patterns in `@am3nd-org/pointcraft-types`
     * so a contract rename cannot drift here. No `businessId` anywhere: the
     * business is derived from the authenticated merchant session.
     */
    payforms: {
      collection: buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.collection),
      item: (experienceId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.item, { experienceId }),
      publish: (experienceId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.publish, { experienceId }),
      disable: (experienceId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.disable, { experienceId }),
      eligibility: (experienceId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.eligibility, { experienceId }),
      submissions: (experienceId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.submissions, { experienceId }),
      submissionItem: (experienceId: string, submissionId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.submissionItem, {
          experienceId,
          submissionId,
        }),
      cancelSubscription: (experienceId: string, submissionId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.cancelSubscription, {
          experienceId,
          submissionId,
        }),
      customers: buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.customers),
      customerSearch: buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.customerSearch),
      customerItem: (customerId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.customerItem, { customerId }),
      customerPayments: (customerId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.customerPayments, { customerId }),
      customerRecurring: (customerId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.customerRecurring, { customerId }),
      customerRecurringCancel: (customerId: string, subscriptionId: string) =>
        buildBusinessPayformPath(BUSINESS_PAYFORM_ROUTES.customerRecurringCancel, {
          customerId,
          subscriptionId,
        }),
      // Payment-method domains of the business's own linked account (DEC-P2-18).
      // No parameters: the account is resolved server-side from the session.
      paymentDomains: buildBusinessPaymentDomainsPath(),
      paymentDomainsEnsure: buildBusinessPaymentDomainsEnsurePath(),
    },
  },
  devices: {
    pairClaim: (pairingCode: string) => `/api/auth/devices/pair/claim/${pairingCode}`,
  },
  // Auth V3 business-device pairing (see docs/authentication/business-device-pairing-v3-client.md).
  // `start` and `complete` are PUBLIC device-side endpoints; `poll` is the PUBLIC status endpoint the
  // device (and the admin tab) reads to watch a session progress; `claim` is the AUTHENTICATED
  // platform-admin endpoint that approves a pending pairing and binds it to a business.
  businessDevicesV3: {
    start: '/api/auth/v3/devices/pair',
    claim: '/api/admin/auth/v3/devices/claim',
    complete: '/api/auth/v3/devices/pair/complete',
    refreshStart: '/api/auth/v3/devices/refresh/start',
    refreshComplete: '/api/auth/v3/devices/refresh/complete',
    migration: '/api/auth/v3/devices/migration',
    poll: (pollingId: string) => `/api/auth/v3/devices/pair/${pollingId}`,
  },
  terminal: {
    ctn_tkn: 'api/services/stp/ctn',
    // Dev/staging-only test harness: mints a real card_present PaymentIntent and
    // returns locationId / stripeAccountId / client_secret for the Manager
    // Tap-to-Pay test page. 400s in production. POST. See
    // pointcraft-backend/docs/frontend/test-stripe-tap-to-pay.md.
    testTap: '/api/admin/test-stripe-tap',
    linked_readers: 'api/services/terminal',
    wake: '/api/services/terminal/wake',
  },
  payouts: {
    fetch: '/api/payouts',
    fetchOne: '/api/payouts/single',
  },
  reports: {
    fetch: '/api/reports/daily-summary/v2',
    syncTransactions: '/api/reports/sync-transactions',
  },
  products: {
    searchIdentifier: '/api/inventory/search',
    create: '/api/inventory',
    base: '/api/inventory',
    bulkUpdateFields: '/api/inventory/bulk/update',
    deleteAll: '/api/inventory/all',
    sold: '/api/inventory/sold',
    massUpdatePrices: '/api/inventory/mass-update-prices',
    massUpdateCategoryAttributes: '/api/inventory/mass-update-category-attributes',
    changes: '/api/inventory/changes',
  },
  payment: {
    attachTPFAAS: '/api/sales/payment/tpfaas/attach',
  },
  customer: {
    base: '/api/customer',
  },
  notify: {
    sendMessage: '/api/notify',
  },
  triggerPayment: '/api/services/stp/present-payment-method',
  quickbooks: {
    getConnectUrl: '/api/services/quickbooks/connect-url',
    getReauthUrl: '/api/services/quickbooks/reauth-url',
    disconnect: '/api/services/quickbooks/disconnect',
    status: '/api/services/quickbooks/status',
    callback: '/api/services/quickbooks/callback',
  },
  paymentLinks: {
    fetch: '/api/sales/payment/link',
    update: (id: string) => `/api/sales/payment/link/${id}`,
    cancel: (id: string) => `/api/sales/payment/link/${id}`,
  },
  // Business Marketplace endpoints (for Sultani Marketplace app)
  marketplace: {
    // Distributors
    distributors: '/api/marketplace/distributors',
    distributorById: (id: string) => `/api/marketplace/distributors/${id}`,
    distributorProducts: (id: string) => `/api/marketplace/distributors/${id}/products`,

    // Products
    products: '/api/marketplace/products',
    productById: (id: string) => `/api/marketplace/products/${id}`,
    productSearch: '/api/marketplace/products/search',

    // Orders
    orders: '/api/marketplace/orders',
    orderById: (id: string) => `/api/marketplace/orders/${id}`,
    orderCancel: (id: string) => `/api/marketplace/orders/${id}/cancel`,

    // Recurring Orders
    recurringOrders: '/api/marketplace/recurring-orders',
    recurringOrderById: (id: string) => `/api/marketplace/recurring-orders/${id}`,
    recurringOrderRun: (id: string) => `/api/marketplace/recurring-orders/${id}/run`,
    recurringOrderPause: (id: string) => `/api/marketplace/recurring-orders/${id}/pause`,
    recurringOrderResume: (id: string) => `/api/marketplace/recurring-orders/${id}/resume`,

    // Notifications
    notifications: '/api/notifications',
    notificationsUnreadCount: '/api/notifications/unread-count',
    notificationMarkRead: (id: string) => `/api/notifications/${id}/read`,
    notificationsMarkAllRead: '/api/notifications/mark-all-read',
    notificationPreferences: '/api/notifications/preferences',
  },
};

// ----------------------------------------------------------------------
